import { AnalyticsApi, ErrorResponse, ProgramAnalytics } from '@citruscamps/citrus-client'
import { Dispatch, useState } from 'react'
import { useQuery } from 'react-query'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import { IDateMacroQuery } from '../../../../../interfaces/DateMacroQuery'
import { generateApiConfig } from '../../../../../utils/client-config'
import { getPastIntervalByQuery } from '../../../../../utils/date-macro'
import { generateBaseKey } from '../../../../../utils/key-generator'

interface IProps {
  programId?: string
  initialDateQuery: IDateMacroQuery
}

interface FetchProgramAnalytics {
  analytics?: ProgramAnalytics
  pastAnalytics?: ProgramAnalytics
  dateQuery: IDateMacroQuery
  isLoading: boolean
  isError: boolean
  error: ErrorResponse | null
  setDateQuery: Dispatch<IDateMacroQuery>
}

export const useFetchProgramAnalytics = ({
  programId,
  initialDateQuery,
}: IProps): FetchProgramAnalytics => {
  const { requestHandler } = useRequestHandler()
  const [dateQuery, setDateQuery] = useState<IDateMacroQuery>(initialDateQuery)
  const queryKeys = generateBaseKey({
    type: 'program_analytics',
    program_id: programId,
    query: dateQuery,
  })
  const {
    isLoading,
    isError,
    data: analytics,
    error,
  } = useQuery<ProgramAnalytics, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new AnalyticsApi(generateApiConfig())
      return await requestHandler<ProgramAnalytics>(() =>
        client.findProgramAnalyticsAnalytics(
          {
            program_id: programId,
            date_macro: dateQuery.date_macro,
            unit: dateQuery.unit,
            start_date:
              typeof dateQuery.start_date === 'string'
                ? new Date(dateQuery.start_date)
                : dateQuery.start_date,
            end_date:
              typeof dateQuery.end_date === 'string'
                ? new Date(dateQuery.end_date)
                : dateQuery.end_date,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          { signal },
        ),
      )
    },
    { enabled: !!programId },
  )

  const interval = getPastIntervalByQuery(dateQuery)
  const pastDateQuery: IDateMacroQuery = {
    ...dateQuery,
    date_macro: 'custom',
    start_date: interval?.start?.toJSDate(),
    end_date: interval?.end?.toJSDate(),
  }
  const pastQueryKeys = generateBaseKey({
    type: 'program_analytics',
    program_id: programId,
    query: pastDateQuery,
  })
  const { data: pastAnalytics } = useQuery<ProgramAnalytics, ErrorResponse>(
    pastQueryKeys,
    async ({ signal }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new AnalyticsApi(generateApiConfig())
      return await requestHandler<ProgramAnalytics>(() =>
        client.findProgramAnalyticsAnalytics(
          {
            program_id: programId,
            date_macro: pastDateQuery.date_macro,
            unit: pastDateQuery.unit,
            start_date:
              pastDateQuery?.start_date && typeof pastDateQuery.start_date === 'string'
                ? new Date(pastDateQuery.start_date)
                : pastDateQuery?.start_date,
            end_date:
              pastDateQuery?.end_date && typeof pastDateQuery.end_date === 'string'
                ? new Date(pastDateQuery.end_date)
                : pastDateQuery?.end_date,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          { signal },
        ),
      )
    },
    { enabled: !!programId && !isLoading && !!analytics },
  )

  return {
    analytics,
    pastAnalytics,
    dateQuery,
    error,
    isLoading,
    isError,
    setDateQuery,
  }
}
